import { useState, useEffect, useCallback } from 'react';
import { taxAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { Receipt, CalendarDays, TrendingUp, TrendingDown, ArrowLeftRight, ChevronLeft, ChevronRight, Eye, RotateCcw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';

const formatCurrency = (val) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val || 0);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border/50 rounded-lg px-4 py-3 shadow-xl">
        <p className="text-xs text-muted-foreground font-mono-data mb-2">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-sm flex justify-between gap-4" style={{ color: p.color }}>
            <span>{p.name}</span>
            <span className="font-bold">{formatCurrency(p.value)}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function UStVAPage() {
  const [period, setPeriod] = useState('monthly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailDialog, setDetailDialog] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await taxAPI.getUStVA(period, year);
      setData(res.data);
    } catch {
      console.error('UStVA load error');
    } finally {
      setLoading(false);
    }
  }, [period, year]);

  useEffect(() => { loadData(); }, [loadData]);

  const openDetails = async (periodItem) => {
    setDetailDialog(periodItem);
    setDetailLoading(true);
    try {
      const res = await taxAPI.getUStVADetails(periodItem.key);
      setDetailData(res.data);
    } catch {
      setDetailData(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const chartData = (data?.periods || [])
    .filter(p => p.umsatzsteuer > 0 || p.vorsteuer > 0 || p.zahllast !== 0)
    .length > 0
    ? data.periods
    : [];

  const currentYr = new Date().getFullYear();
  const yearOptions = [currentYr - 1, currentYr, currentYr + 1];

  return (
    <div className="space-y-8" data-testid="ustva-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">Umsatzsteuer</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Umsatzsteuervoranmeldung – Übersicht und Berechnung</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[160px] bg-background" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monatlich</SelectItem>
              <SelectItem value="quarterly">Vierteljährlich</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => setYear(y => y - 1)} data-testid="year-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-[100px] bg-background" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={() => setYear(y => y + 1)} data-testid="year-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="ustva-summary-cards">
          <Card className="bg-card border border-red-500/20">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-red-400" />
                </div>
                <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Umsatzsteuer</span>
              </div>
              <div className="font-mono-data text-2xl font-bold text-red-400" data-testid="total-ust">
                {formatCurrency(data.totals.umsatzsteuer)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Aus Ausgangsrechnungen {year}</p>
            </CardContent>
          </Card>

          <Card className="bg-card border border-emerald-500/20">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Vorsteuer</span>
              </div>
              <div className="font-mono-data text-2xl font-bold text-emerald-400" data-testid="total-vst">
                {formatCurrency(data.totals.vorsteuer)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Aus Eingangsrechnungen {year}</p>
            </CardContent>
          </Card>

          <Card className={`bg-card border ${data.totals.zahllast >= 0 ? 'border-amber-500/20' : 'border-primary/20'}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg ${data.totals.zahllast >= 0 ? 'bg-amber-500/10' : 'bg-primary/10'} flex items-center justify-center`}>
                  <ArrowLeftRight className={`w-5 h-5 ${data.totals.zahllast >= 0 ? 'text-amber-400' : 'text-primary'}`} />
                </div>
                <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Zahllast</span>
              </div>
              <div className={`font-mono-data text-2xl font-bold ${data.totals.zahllast >= 0 ? 'text-amber-400' : 'text-primary'}`} data-testid="total-zahllast">
                {formatCurrency(data.totals.zahllast)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {data.totals.zahllast >= 0 ? 'An Finanzamt zu zahlen' : 'Erstattung vom Finanzamt'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Chart */}
      {chartData.length > 0 && (
        <Card className="bg-card border border-border/50" data-testid="ustva-chart">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-primary" />
              USt / VSt Verlauf – {year}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-52 sm:h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.periods || []} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(217, 33%, 17%)" />
                  <XAxis dataKey="label" stroke="#94A3B8" fontSize={10} fontFamily="JetBrains Mono" angle={-30} textAnchor="end" height={50} />
                  <YAxis stroke="#94A3B8" fontSize={11} fontFamily="JetBrains Mono" tickFormatter={(v) => `${v}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="3 3" />
                  <Bar dataKey="umsatzsteuer" name="Umsatzsteuer" fill="#f87171" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="vorsteuer" name="Vorsteuer" fill="#34d399" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="zahllast" name="Zahllast" fill="#fbbf24" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Period Table */}
      <Card className="bg-card border border-border/50" data-testid="ustva-table">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Receipt className="w-4 h-4 text-primary" />
            {period === 'monthly' ? 'Monatliche' : 'Vierteljährliche'} Übersicht – {year}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Laden...</div>
          ) : (
            <div className="table-responsive">
            <Table>
              <TableHeader>
                <TableRow className="border-border/30">
                  <TableHead>Zeitraum</TableHead>
                  <TableHead className="text-right hidden sm:table-cell">Netto Umsatz</TableHead>
                  <TableHead className="text-right">Umsatzsteuer</TableHead>
                  <TableHead className="text-right hidden sm:table-cell">Netto Kosten</TableHead>
                  <TableHead className="text-right">Vorsteuer</TableHead>
                  <TableHead className="text-right">Zahllast</TableHead>
                  <TableHead className="text-right">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.periods || []).map((p) => {
                  const hasData = p.umsatzsteuer > 0 || p.vorsteuer > 0;
                  return (
                    <TableRow
                      key={p.key}
                      className={`border-border/20 ${hasData ? 'hover:bg-accent/50' : 'opacity-50'}`}
                      data-testid={`ustva-row-${p.key}`}
                    >
                      <TableCell className="font-medium">{p.label}</TableCell>
                      <TableCell className="text-right font-mono-data text-sm hidden sm:table-cell">{formatCurrency(p.net_revenue)}</TableCell>
                      <TableCell className="text-right font-mono-data text-sm text-red-400">{formatCurrency(p.umsatzsteuer)}</TableCell>
                      <TableCell className="text-right font-mono-data text-sm hidden sm:table-cell">{formatCurrency(p.net_costs)}</TableCell>
                      <TableCell className="text-right font-mono-data text-sm text-emerald-400">{formatCurrency(p.vorsteuer)}</TableCell>
                      <TableCell className="text-right">
                        <span className={`font-mono-data text-sm font-bold ${p.zahllast >= 0 ? 'text-amber-400' : 'text-primary'}`}>
                          {formatCurrency(p.zahllast)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {hasData && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetails(p)}
                            className="text-muted-foreground hover:text-foreground"
                            data-testid={`ustva-details-${p.key}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Totals row */}
                <TableRow className="border-t-2 border-primary/30 font-bold">
                  <TableCell>Gesamt {year}</TableCell>
                  <TableCell className="text-right font-mono-data text-sm hidden sm:table-cell">
                    {formatCurrency((data?.periods || []).reduce((s, p) => s + p.net_revenue, 0))}
                  </TableCell>
                  <TableCell className="text-right font-mono-data text-sm text-red-400">
                    {formatCurrency(data?.totals?.umsatzsteuer)}
                  </TableCell>
                  <TableCell className="text-right font-mono-data text-sm hidden sm:table-cell">
                    {formatCurrency((data?.periods || []).reduce((s, p) => s + p.net_costs, 0))}
                  </TableCell>
                  <TableCell className="text-right font-mono-data text-sm text-emerald-400">
                    {formatCurrency(data?.totals?.vorsteuer)}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`font-mono-data text-sm font-bold ${(data?.totals?.zahllast || 0) >= 0 ? 'text-amber-400' : 'text-primary'}`}>
                      {formatCurrency(data?.totals?.zahllast)}
                    </span>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      {detailDialog && (
        <Dialog open={!!detailDialog} onOpenChange={() => { setDetailDialog(null); setDetailData(null); }}>
          <DialogContent className="bg-card border-border/50 max-w-3xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-auto" data-testid="ustva-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                UStVA Detail: {detailDialog.label}
              </DialogTitle>
            </DialogHeader>

            {detailLoading ? (
              <div className="py-8 text-center text-muted-foreground">Laden...</div>
            ) : detailData ? (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Umsatzsteuer</p>
                    <p className="font-mono-data font-bold text-red-400">{formatCurrency(detailData.umsatzsteuer)}</p>
                    {detailData.rc_umsatzsteuer > 0 && (
                      <p className="text-[10px] text-violet-400 mt-1">davon §13b: {formatCurrency(detailData.rc_umsatzsteuer)}</p>
                    )}
                  </div>
                  <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Vorsteuer</p>
                    <p className="font-mono-data font-bold text-emerald-400">{formatCurrency(detailData.vorsteuer)}</p>
                    {detailData.rc_vorsteuer > 0 && (
                      <p className="text-[10px] text-violet-400 mt-1">davon §13b: {formatCurrency(detailData.rc_vorsteuer)}</p>
                    )}
                  </div>
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Zahllast</p>
                    <p className={`font-mono-data font-bold ${(detailData.umsatzsteuer - detailData.vorsteuer) >= 0 ? 'text-amber-400' : 'text-primary'}`}>
                      {formatCurrency(detailData.umsatzsteuer - detailData.vorsteuer)}
                    </p>
                  </div>
                </div>

                {/* Outgoing Invoices */}
                {detailData.outgoing_invoices?.length > 0 && (
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-red-400 mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Ausgangsrechnungen (Umsatzsteuer)
                    </h3>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border/30">
                          <TableHead className="text-xs">Nr.</TableHead>
                          <TableHead className="text-xs">Kontakt</TableHead>
                          <TableHead className="text-xs">Datum</TableHead>
                          <TableHead className="text-xs text-right">Netto</TableHead>
                          <TableHead className="text-xs text-right">USt</TableHead>
                          <TableHead className="text-xs text-right">Brutto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detailData.outgoing_invoices.map((inv) => (
                          <TableRow key={inv.id} className="border-border/20">
                            <TableCell className="font-mono-data text-xs text-primary">{inv.invoice_number}</TableCell>
                            <TableCell className="text-sm">{inv.contact_name}</TableCell>
                            <TableCell className="font-mono-data text-xs">{inv.invoice_date}</TableCell>
                            <TableCell className="text-right font-mono-data text-xs">{formatCurrency(inv.net_total)}</TableCell>
                            <TableCell className="text-right font-mono-data text-xs text-red-400">{formatCurrency(inv.tax_amount)}</TableCell>
                            <TableCell className="text-right font-mono-data text-xs font-bold">{formatCurrency(inv.gross_total)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <Separator className="bg-border/30" />

                {/* Incoming Invoices */}
                {detailData.incoming_invoices?.length > 0 && (
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                      <TrendingDown className="w-4 h-4" />
                      Eingangsrechnungen (Vorsteuer)
                    </h3>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border/30">
                          <TableHead className="text-xs">Lieferant</TableHead>
                          <TableHead className="text-xs">Nr.</TableHead>
                          <TableHead className="text-xs">Datum</TableHead>
                          <TableHead className="text-xs text-right">Netto</TableHead>
                          <TableHead className="text-xs text-right">VSt</TableHead>
                          <TableHead className="text-xs text-right">Brutto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detailData.incoming_invoices.map((inv) => (
                          <TableRow key={inv.id} className="border-border/20">
                            <TableCell className="text-sm">{inv.vendor_name}</TableCell>
                            <TableCell className="font-mono-data text-xs">{inv.invoice_number}</TableCell>
                            <TableCell className="font-mono-data text-xs">{inv.invoice_date}</TableCell>
                            <TableCell className="text-right font-mono-data text-xs">{formatCurrency(inv.net_amount)}</TableCell>
                            <TableCell className="text-right font-mono-data text-xs text-emerald-400">{formatCurrency(inv.tax_amount)}</TableCell>
                            <TableCell className="text-right font-mono-data text-xs font-bold">{formatCurrency(inv.gross_amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Reverse-Charge Invoices */}
                {detailData.reverse_charge_invoices?.length > 0 && (
                  <div>
                    <Separator className="bg-border/30 my-2" />
                    <h3 className="font-heading text-sm font-semibold text-violet-400 mb-2 flex items-center gap-2">
                      <RotateCcw className="w-4 h-4" />
                      Reverse-Charge §13b UStG (Nullsummenspiel)
                    </h3>
                    <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-3 mb-3 text-xs text-muted-foreground">
                      Diese Rechnungen werden sowohl als Umsatzsteuer als auch als Vorsteuer ausgewiesen – die Zahllast bleibt unverändert.
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border/30">
                          <TableHead className="text-xs">Lieferant</TableHead>
                          <TableHead className="text-xs">Nr.</TableHead>
                          <TableHead className="text-xs">Datum</TableHead>
                          <TableHead className="text-xs text-right">Netto</TableHead>
                          <TableHead className="text-xs text-right">§13b USt</TableHead>
                          <TableHead className="text-xs text-right">§13b VSt</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detailData.reverse_charge_invoices.map((inv) => (
                          <TableRow key={inv.id} className="border-border/20">
                            <TableCell className="text-sm">{inv.vendor_name}</TableCell>
                            <TableCell className="font-mono-data text-xs">{inv.invoice_number}</TableCell>
                            <TableCell className="font-mono-data text-xs">{inv.invoice_date}</TableCell>
                            <TableCell className="text-right font-mono-data text-xs">{formatCurrency(inv.net_amount)}</TableCell>
                            <TableCell className="text-right font-mono-data text-xs text-red-400">+{formatCurrency(inv.reverse_charge_tax_amount)}</TableCell>
                            <TableCell className="text-right font-mono-data text-xs text-emerald-400">-{formatCurrency(inv.reverse_charge_tax_amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {detailData.outgoing_invoices?.length === 0 && detailData.incoming_invoices?.length === 0 && !detailData.reverse_charge_invoices?.length && (
                  <p className="text-center text-muted-foreground py-4">Keine Rechnungen in diesem Zeitraum</p>
                )}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">Fehler beim Laden der Details</p>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
